import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, Injector, input, OnInit, signal } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { DetailsForm } from '../../_models/event-form.model';
import { CreateEventFormStore } from '../../new-event-form/_services/create-event-form-store.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { formatDate } from '@angular/common';
import { firstValueFrom, tap } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { SimpleTextDialogComponent } from 'src/app/components/simple-text-dialog/simple-text-dialog.component';
import { SimpleTextDialogData } from 'src/app/components/simple-text-dialog/models/simple-text-dialog-data.model';
import { MatDialog } from '@angular/material/dialog';
import { EditEventFormStore } from '../../edit-event-form/_services/edit-event-form-store.service';

@Component({
  selector: 'details-step',
  standalone: false,
  templateUrl: './details-step.component.html',
  styleUrl: './details-step.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,

})
export class DetailsStepComponent implements OnInit {
  edit = input<boolean>(false);

  public destroyRef = inject(DestroyRef);

  public formGroup = input.required<FormGroup<DetailsForm>>();

  private injector = inject(Injector);

  public eventFormStore = computed(() =>
    this.edit()
      ? this.injector.get(EditEventFormStore)
      : this.injector.get(CreateEventFormStore)
  );

  public readonly translate = inject(TranslateService);

  public readonly dialog = inject(MatDialog);

  public linkedEvent = computed(() => !!this.eventFormStore().streappEvent().linkedToMainStreappID);

  public maxDate: string;

  public minDate: string;

  public isLoadingAiSuggestions = signal<boolean>(false);

  constructor() {
    const max = new Date();
    this.maxDate = formatDate(max, 'yyyy-MM-dd', 'en');
    this.maxDate += 'T' + formatDate(new Date(), 'HH:mm', 'en');

    const min = new Date();
    min.setDate(min.getDate() - 2);
    this.minDate = formatDate(min, 'yyyy-MM-dd', 'en');
    this.minDate += 'T' + formatDate(new Date(), 'HH:mm', 'en');
  }

  public ngOnInit(): void {
    this.formGroup().valueChanges.pipe(
      takeUntilDestroyed(this.destroyRef),
      tap(value => {
        this.eventFormStore().setDetailsFormValue(value)
      })
    ).subscribe()

    this.formGroup().statusChanges.pipe(
      takeUntilDestroyed(this.destroyRef),
      tap(value => this.eventFormStore().stepDetailsValid.set(value === 'VALID'))
    ).subscribe()
  }

  async getTextSuggestion(): Promise<void> {
    var title = this.formGroup().get('titel')?.value;
    var description = this.formGroup().get('beschrijving')?.value;

    if((!title && !this.linkedEvent()) || !description || this.isLoadingAiSuggestions()) return;

    this.isLoadingAiSuggestions.set(true);
    
    try {
      const result = await firstValueFrom(this.eventFormStore().streappService.getTextSuggestion(title, description));

      if(!this.linkedEvent()) {
        const titleQuestion = await firstValueFrom(
          this.translate.get('event_steps.step_details.text_suggestion_dialog.title_question')
        );

        const useTitle = await firstValueFrom(
          this.dialog.open<SimpleTextDialogComponent, SimpleTextDialogData>(
            SimpleTextDialogComponent,
            {
              data: {
                title: 'event_steps.step_details.text_suggestion_dialog.title_title',
                text: `<b>${titleQuestion}</b><br><br>${result[1]}`,
                showButton: true,
                showCancelButton: true,
                buttonCancelText: 'Nee',
                buttonText: 'Ja',
              }
            }
          )
          .afterClosed()
        );

        if (useTitle) {
          this.formGroup().patchValue({
            titel: result[1],
          });
        }
      }

      const descriptionQuestion = await firstValueFrom(
        this.translate.get('event_steps.step_details.text_suggestion_dialog.title_question')
      );

      const useDescription = await firstValueFrom(
        this.dialog.open<SimpleTextDialogComponent, SimpleTextDialogData>(
          SimpleTextDialogComponent,
          {
            data: {
              title: 'event_steps.step_details.text_suggestion_dialog.title_description',
              text: `<b>${descriptionQuestion}</b><br><br>${result[0]}`,
              showButton: true,
              showCancelButton: true,
              buttonCancelText: 'Nee',
              buttonText: 'Ja',
            }
          }
        )
        .afterClosed()
      );
      
      if (useDescription) {
        this.formGroup().patchValue({
          beschrijving: result[0],
        });
      }
    } catch (error) {
      console.error(error);
    } finally {
      this.isLoadingAiSuggestions.set(false);
    }
  }
}
